import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { STAGES } from "@/data/course";
import {
  ArrowRight, BookOpen, CandlestickChart, Wallet, Calculator, MessageSquare,
  GraduationCap, TrendingUp, ShieldCheck, ChevronRight,
} from "lucide-react";

const FEATURES = [
  { icon: BookOpen, title: "15-Module Course", desc: "From 'what is trading' to building and testing a full strategy.", to: "/course" },
  { icon: CandlestickChart, title: "Interactive Charts", desc: "Real crypto data and multiple timeframes with indicators.", to: "/charts" },
  { icon: Wallet, title: "$100k Paper Simulator", desc: "Practice long & short trades with virtual money — zero risk.", to: "/simulator" },
  { icon: Calculator, title: "Risk Calculator", desc: "Learn position sizing and protect your (virtual) capital.", to: "/calculator" },
  { icon: MessageSquare, title: "AI Trading Tutor", desc: "Ask anything and get clear, educational answers.", to: "/tutor" },
  { icon: GraduationCap, title: "Quizzes & XP", desc: "Test yourself, earn XP and level up from Novice to Trader.", to: "/quizzes" },
];

export default function Home() {
  const { user, progress } = useAuth();

  return (
    <div className="space-y-10">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-slate-800 animate-in">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="market" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#090D16] via-[#090D16]/85 to-blue-950/40" />
        </div>
        <div className="relative px-6 sm:px-12 py-14 sm:py-20 max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-blue-300 bg-blue-500/10 border border-blue-500/25 rounded-full px-3 py-1 mb-6">
            <ShieldCheck className="w-3.5 h-3.5" /> 100% Educational · Risk-Free
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Learn Trading<br /><span className="text-blue-400">From Zero.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
            Learn how markets work, how to read charts, build strategies and manage risk — step by step. Then practice everything in a $100,000 virtual simulator.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/course" data-testid="start-course-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl px-6 py-3 transition-colors glow-primary">
              Start the Course <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/simulator" data-testid="try-simulator-btn" className="inline-flex items-center gap-2 bg-slate-800/80 hover:bg-slate-700 border border-slate-700 text-slate-100 font-semibold rounded-xl px-6 py-3 transition-colors">
              Try the Simulator
            </Link>
          </div>
          {user && progress && (
            <div className="mt-8 inline-flex items-center gap-3 text-sm bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-2.5">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-slate-300">Welcome back, <span className="text-white font-semibold">{user.name}</span> — you're <span className="text-blue-400 font-semibold">{progress.course_percent}%</span> through the course.</span>
            </div>
          )}
        </div>
      </section>

      {/* Roadmap */}
      <section>
        <h2 className="font-display text-2xl sm:text-3xl font-bold mb-1">Your learning path</h2>
        <p className="text-slate-400 text-sm mb-6">A progressive journey from complete beginner to simulation trader.</p>
        <div className="flex flex-wrap items-center gap-2">
          {STAGES.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2 bg-slate-900/70 border border-slate-800 rounded-full pl-2.5 pr-4 py-1.5">
                <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold grid place-items-center font-mono">{i + 1}</span>
                <span className="text-sm font-medium text-slate-200">{s}</span>
              </div>
              {i < STAGES.length - 1 && <ChevronRight className="w-4 h-4 text-slate-600" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      <DisclaimerBanner />

      {/* Features */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map((f) => {
          const Icon = f.icon;
          return (
            <Link key={f.title} to={f.to} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group bg-[#0F172A] border border-slate-800 rounded-2xl p-6 hover:border-blue-500/50 hover:-translate-y-0.5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/20 grid place-items-center mb-4 group-hover:bg-blue-500/25 transition-colors">
                <Icon className="w-5 h-5 text-blue-300" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-1">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </Link>
          );
        })}
      </section>

      <section className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-[#0B1120] p-8 text-center">
        <h2 className="font-display text-2xl font-bold mb-2">No hype. No promises. Just learning.</h2>
        <p className="text-slate-400 max-w-2xl mx-auto text-sm leading-relaxed">
          Trading is not a fast or easy way to make money, and no strategy is guaranteed. This platform teaches you the fundamentals honestly and lets you practice safely with virtual funds.
        </p>
      </section>
    </div>
  );
}
